import { PARENT_FINANCIAL_AID_ATTESTATION_KEYS } from './financialAidAttestations';

export type ParentFinancialAidCopyCertificationContext = {
  certifiedAccurate?: boolean | null;
  agreedToTerms?: boolean | null;
  electronicSignature?: string | null;
  certificationDate?: string | null;
};

export type ParentFinancialAidCopySignatureStatus =
  | 'captured'
  | 'not_captured'
  | 'not_required';

const hasSubmittedCertification = (
  certification: ParentFinancialAidCopyCertificationContext | null | undefined,
): boolean => Boolean(
  certification?.certifiedAccurate
    && certification.agreedToTerms
    && String(certification.electronicSignature ?? '').trim(),
);

export const formatParentFinancialAidCopyCertificationStatus = (
  certification: ParentFinancialAidCopyCertificationContext | null | undefined,
): string => (
  hasSubmittedCertification(certification)
    ? 'Captured electronically'
    : 'Not captured'
);

export const formatParentFinancialAidCopyCertificationDate = (
  value: string | null | undefined,
): string => {
  const text = String(value ?? '').trim();
  if (!text) return '';

  const dateOnlyMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    return `${Number(dateOnlyMatch[2])}/${Number(dateOnlyMatch[3])}/${dateOnlyMatch[1]}`;
  }

  const date = new Date(text);
  if (!Number.isNaN(date.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(text)) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  return text;
};

export const resolveParentFinancialAidCopyCertificationName = ({
  fatherName,
  fatherComplete,
  fatherSigned,
  motherName,
  motherComplete,
  motherSigned,
}: {
  fatherName?: string | null;
  fatherComplete: boolean;
  fatherSigned?: boolean | null;
  motherName?: string | null;
  motherComplete: boolean;
  motherSigned?: boolean | null;
}): string => {
  const normalizedFatherName = String(fatherName ?? '').trim();
  const normalizedMotherName = String(motherName ?? '').trim();
  const signedNames = [
    fatherSigned ? (normalizedFatherName || 'Father/guardian') : null,
    motherSigned ? (normalizedMotherName || 'Mother/guardian') : null,
  ].filter(Boolean);

  if (signedNames.length > 0) return signedNames.join(' and ');
  if (fatherComplete && normalizedFatherName) return normalizedFatherName;
  if (motherComplete && normalizedMotherName) return normalizedMotherName;
  return normalizedFatherName || normalizedMotherName || 'Parent/guardian';
};

export const resolveParentFinancialAidCopySignatureStatus = ({
  parentComplete,
  localSignature,
  captured,
}: {
  parentComplete: boolean;
  localSignature?: string | null;
  captured?: boolean | null;
}): ParentFinancialAidCopySignatureStatus => {
  if (captured) return 'captured';
  if (String(localSignature ?? '').trim()) return 'captured';
  if (!parentComplete) return 'not_required';
  return 'not_captured';
};

export const formatParentFinancialAidCopySignatureStatus = (
  status: ParentFinancialAidCopySignatureStatus,
): string => {
  if (status === 'captured') return 'Captured electronically';
  if (status === 'not_required') return 'Not required';
  return 'Not captured';
};

export const didParentFinancialAidCopyCompleteAttestations = (
  localAttestations: Record<string, unknown>,
  certification?: ParentFinancialAidCopyCertificationContext | null,
): boolean => (
  PARENT_FINANCIAL_AID_ATTESTATION_KEYS.every((key) => Boolean(localAttestations[key]))
    || hasSubmittedCertification(certification)
);
